import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SeasonPill } from "@/components/SeasonCountdown";
import { TEST_IDS } from "@/lib/testIds";
import {
  Gauge, Users, Question, Broadcast, Trophy, Crosshair, Skull, CalendarBlank,
  Crown, MapPin, Gift, Handshake, ChatsCircle, Flag, ChartLine, GearSix, CalendarStar,
  ShieldStar, Power, ArrowSquareOut, Megaphone, Recycle, List, X, Bug, Books,
} from "@phosphor-icons/react";

const NAV_GROUPS = [
  {
    title: "Overview",
    items: [{ to: "/admin", end: true, label: "Dashboard", Icon: Gauge }],
  },
  {
    title: "Content",
    items: [
      { to: "/admin/nke", label: "Knowledge Engine", Icon: Books, gold: true },
      // Legacy Question Bank retired in Phase 4 (routes still accessible via URL for rollback):
      //   /admin/questions, /admin/heatmap, /admin/used-bank
    ],
  },
  {
    title: "Game Modes",
    items: [
      { to: "/admin/daily", label: "NK Daily", Icon: Broadcast },
      { to: "/admin/ladder", label: "NK Ladder", Icon: Trophy },
      { to: "/admin/duel", label: "NK Duel", Icon: Crosshair },
      { to: "/admin/survival", label: "NK Escape", Icon: Skull },
      { to: "/admin/saturday", label: "Saturday Challenge", Icon: CalendarStar, gold: true },
      { to: "/admin/championship", label: "Championship Ops", Icon: Crown, gold: true },
      { to: "/admin/live-hosts", label: "Live Hosts", Icon: Crown, gold: true },
      { to: "/admin/modes-status", label: "Mode Status", Icon: Power },
    ],
  },
  {
    title: "Players",
    items: [
      { to: "/admin/players", label: "Players", Icon: Users },
      { to: "/admin/moderation", label: "Chat & Moderation", Icon: ChatsCircle },
      { to: "/admin/reports", label: "Reports", Icon: Flag },
      { to: "/admin/feedback", label: "Beta Feedback", Icon: Bug },
    ],
  },
  {
    title: "Country",
    items: [
      { to: "/admin/countries", label: "Countries", Icon: Flag },
      { to: "/admin/country-analytics", label: "Country Analytics", Icon: ChartLine },
      { to: "/admin/country-war", label: "Country War", Icon: MapPin },
      { to: "/admin/expansion-watchlist", label: "Expansion Watchlist", Icon: MapPin, gold: true },
      { to: "/admin/ambassadors", label: "Ambassadors", Icon: Megaphone },
    ],
  },
  {
    title: "Season & Rewards",
    items: [
      { to: "/admin/seasons", label: "Seasons", Icon: CalendarBlank },
      { to: "/admin/rewards", label: "Payouts", Icon: Gift, gold: true },
      { to: "/admin/verifications", label: "Verifications", Icon: ShieldStar, gold: true },
    ],
  },
  {
    title: "Partnerships",
    items: [
      { to: "/admin/sponsors", label: "Sponsors", Icon: Handshake },
      { to: "/admin/ads", label: "House Ads", Icon: Megaphone },
      { to: "/admin/partner-inquiries", label: "Partner Inquiries", Icon: ChatsCircle },
    ],
  },
  {
    title: "Communications",
    items: [
      { to: "/admin/signals", label: "Signals", Icon: Megaphone, gold: true },
    ],
  },
  {
    title: "System",
    items: [
      { to: "/admin/analytics", label: "Analytics", Icon: ChartLine },
      { to: "/admin/admins", label: "Administrators", Icon: ShieldStar, gold: true },
      { to: "/admin/settings", label: "Settings", Icon: GearSix },
    ],
  },
];

const NAV = NAV_GROUPS.flatMap((g) => g.items);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = NAV.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
  const currentGroup = NAV_GROUPS.find((g) => g.items.some((i) => (i.end ? pathname === i.to : pathname.startsWith(i.to))));
  const isSuperAdmin = user?.role === "super_admin";
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  return (
    <div className="nk-arena min-h-screen text-nk-text font-sans relative">
      <div className="relative z-10">
      <header
        data-testid="admin-header"
        className="sticky top-0 z-30 border-b border-white/8 bg-nk-charcoal/85 backdrop-blur-xl"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          {/* Mobile menu trigger */}
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="admin-mobile-menu"
            className="lg:hidden p-2 -ml-2 text-nk-text hover:text-nk-accent transition-colors"
            aria-label="Open admin menu"
          >
            <List size={18} weight="bold" />
          </button>

          <button onClick={() => navigate("/admin")} className="flex items-center gap-2 sm:gap-3 group min-w-0">
            <div
              className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg, #0B3D2E 0%, #13593F 100%)", border: "1px solid rgba(212,175,55,0.6)" }}
            >
              <span className="font-display font-bold text-nk-gold text-sm">N</span>
            </div>
            <span className="font-display text-base sm:text-xl font-medium tracking-tight truncate text-white">
              NKognito
              <span className="hidden sm:inline text-nk-gold/80 ml-2 text-sm">· Command Center</span>
            </span>
          </button>

          <div className="hidden md:block"><SeasonPill /></div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate("/home")}
              className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-white/55 hover:text-white border border-white/10 hover:border-nk-gold px-2.5 py-1.5 rounded-sm transition-colors"
              title="Open the player site"
              data-testid="admin-view-player-site"
            >
              <ArrowSquareOut size={11} weight="bold" /> Player site
            </button>
            <div
              className="hidden sm:flex items-center gap-2 px-2 sm:px-3 py-1.5 border border-nk-gold/40 rounded-sm max-w-[180px]"
              title={user?.email}
            >
              <ShieldStar size={14} weight="duotone" className="text-nk-gold shrink-0" />
              <span className="text-xs sm:text-sm truncate text-white">
                {user?.name || user?.email?.split("@")[0]}
              </span>
              <span className="hidden sm:inline text-[10px] text-nk-gold font-mono shrink-0 uppercase">
                {isSuperAdmin ? "super" : "admin"}
              </span>
            </div>
            <button
              data-testid={TEST_IDS.logoutBtn}
              onClick={logout}
              className="p-2 border border-white/10 rounded-sm hover:border-nk-bad hover:text-nk-bad transition-colors shrink-0"
              title="Sign out"
              aria-label="Sign out"
            >
              <Power size={14} weight="bold" />
            </button>
          </div>
        </div>

        {/* Mobile breadcrumb row */}
        <div className="lg:hidden border-t border-white/8 px-4 py-2 flex items-center justify-between gap-2 bg-nk-charcoal/85">
          <div className="min-w-0 flex items-center gap-2">
            <p className="text-[10px] text-white/40 font-mono uppercase tracking-wider shrink-0">
              {currentGroup?.title || "Admin"}
            </p>
            <span className="text-white/40 text-[10px]">/</span>
            <p className="text-sm font-medium truncate text-white" data-testid="admin-mobile-breadcrumb">
              {current?.label || "Dashboard"}
            </p>
          </div>
          <SeasonPill />
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-0 lg:gap-6">
          {/* Desktop vertical sidebar */}
          <aside className="hidden lg:block lg:sticky lg:top-24 lg:self-start">
            <p className="font-mono text-[10px] text-nk-dim uppercase tracking-[0.18em] px-3 mb-3">
              Command Center
            </p>
            <SidebarNav testidPrefix="admin-nav" onNavigate={() => {}} />
          </aside>

          <main className="min-w-0 animate-fade-in">
            <Outlet />
          </main>
        </div>
      </div>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="admin-mobile-drawer">
          <button
            className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
          />
          <div className="absolute left-0 top-0 bottom-0 w-[88%] max-w-[340px] bg-nk-charcoal border-r border-white/10 shadow-2xl overflow-y-auto animate-slide-in-left">
            <div className="sticky top-0 bg-nk-charcoal/95 backdrop-blur-md p-4 border-b border-white/8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldStar size={16} weight="fill" className="text-nk-gold" />
                <p className="font-display text-base font-medium">Command Center</p>
              </div>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close"
                      data-testid="admin-mobile-drawer-close"
                      className="p-1.5 text-nk-mute hover:text-nk-text">
                <X size={16} weight="bold" />
              </button>
            </div>
            <nav className="p-3 pb-8">
              <SidebarNav testidPrefix="admin-drawer-nav" onNavigate={() => setDrawerOpen(false)} />
              <div className="mt-6 pt-4 border-t border-nk-line/40 space-y-1">
                <Link to="/home" data-testid="admin-drawer-player-site"
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-nk-mute hover:text-nk-text rounded-sm">
                  <ArrowSquareOut size={14} weight="bold" /> Open player site
                </Link>
                <button onClick={logout}
                        data-testid="admin-drawer-logout"
                        className="flex items-center gap-2.5 px-3 py-2 text-sm text-nk-bad hover:text-nk-bad/80 w-full text-left rounded-sm">
                  <Power size={14} weight="bold" /> Sign out
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarNav({ onNavigate, testidPrefix = "admin-nav" }) {
  return (
    <nav className="flex flex-col gap-4" aria-label="Admin navigation">
      {NAV_GROUPS.map((group) => (
        <div key={group.title}>
          <p className="text-[9px] text-white/40 uppercase tracking-[0.22em] px-3 mb-1.5">{group.title}</p>
          <div className="flex flex-col gap-0.5">
            {group.items.map(({ to, end, label, Icon, gold }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={onNavigate}
                data-testid={`${testidPrefix}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm px-3 py-2 rounded-sm flex items-center gap-2.5 transition-all ${
                    isActive
                      ? `bg-nk-green/40 ${gold ? "text-nk-gold border-l-2 border-nk-gold" : "text-white border-l-2 border-nk-gold"}`
                      : `${gold ? "text-nk-gold/80 hover:text-nk-gold" : "text-white/55 hover:text-white"} border-l-2 border-transparent`
                  }`
                }
              >
                <Icon size={15} weight={gold ? "fill" : "duotone"} />
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}
